import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import useAutoRefresh from "@/lib/useAutoRefresh";
import {
  FileText,
  Phone,
  ChevronRight,
  Edit3,
  Image as ImageIcon,
  Eye,
  Shield,
  Home as HomeIcon,
  Handshake,
  Download,
  HelpCircle,
  AlarmClockOff,
  Search as SearchIcon,
  Sparkles,
} from "lucide-react";
import { api, BRL, formatDateBR, HERO_IMAGE } from "@/lib/api";
import { FdpPromoCard } from "@/components/FdpPromoCard";
import InstallAppDialog, { isStandalone } from "@/components/InstallAppDialog";
import PriceConsultationDialog from "@/components/PriceConsultationDialog";

const STATUS_META = {
  aguardando: { label: "Aguardando", bg: "bg-orange-100", text: "text-orange-700" },
  aprovado: { label: "Aprovado", bg: "bg-green-100", text: "text-green-700" },
  reprovado: { label: "Cancelado", bg: "bg-red-100", text: "text-red-700" },
  iniciando: { label: "Em andamento", bg: "bg-blue-100", text: "text-blue-700" },
  finalizado: { label: "Finalizado", bg: "bg-emerald-100", text: "text-emerald-700" },
  nota_fiscal_gerada: { label: "NF Gerada", bg: "bg-indigo-100", text: "text-indigo-700" },
  nota_faturada: { label: "NF Paga", bg: "bg-violet-100", text: "text-violet-700" },
  deletado: { label: "Deletado", bg: "bg-slate-200", text: "text-slate-600" },
  validade_expirada: { label: "Vencido", bg: "bg-orange-100", text: "text-orange-800" },
};

const TABS = [
  { key: "todos", label: "Todos" },
  { key: "aguardando", label: "Aguardando" },
  { key: "aprovado", label: "Aprovados" },
  { key: "reprovado", label: "Cancelados" },
  { key: "deletado", label: "Deletados" },
];

export default function SchoolDashboard() {
  const { me } = useOutletContext();
  const nav = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [fdp, setFdp] = useState(null);
  const [fiados, setFiados] = useState([]);
  const [installOpen, setInstallOpen] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const standalone = isStandalone();

  const refreshAll = () => {
    api.get("/school/budgets").then((r) => setBudgets(r.data)).catch(() => {});
    api.get("/school/fdp").then((r) => setFdp(r.data)).catch(() => {});
    api.get("/school/fiado-budgets").then((r) => setFiados(r.data)).catch(() => {});
  };
  useEffect(() => { refreshAll(); }, []);
  useAutoRefresh(refreshAll, 15000);

  const fiadoTotal = useMemo(
    () => fiados.reduce((s, b) => s + (b.total || 0), 0),
    [fiados]
  );

  const counts = useMemo(() => {
    const o = { todos: budgets.length };
    for (const t of TABS) if (t.key !== "todos") o[t.key] = 0;
    for (const b of budgets) o[b.status] = (o[b.status] || 0) + 1;
    return o;
  }, [budgets]);

  const recent = useMemo(() => budgets.slice(0, 3), [budgets]);

  const openCount = budgets.filter((b) => b.status === "aguardando").length;
  const expiredCount = budgets.filter((b) => b.status === "validade_expirada").length;
  const firstWaiting = budgets.find((b) => b.status === "aguardando");

  // Grid "Orçamentos por status" (visão da escola) — igual ao Admin.
  // Mostra só os status que REALMENTE existem na escola, pra não poluir.
  const STATUS_GRID = [
    { key: "aguardando",         label: "Aguardando",   color: "bg-orange-100 text-orange-700 border-orange-200" },
    { key: "aprovado",           label: "Aprovado",     color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    { key: "iniciando",          label: "Em andamento", color: "bg-blue-100 text-blue-700 border-blue-200" },
    { key: "finalizado",         label: "Finalizado",   color: "bg-teal-100 text-teal-700 border-teal-200" },
    { key: "nota_fiscal_gerada", label: "NF Gerada",    color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    { key: "nota_faturada",      label: "NF Paga",      color: "bg-violet-100 text-violet-700 border-violet-200" },
    { key: "validade_expirada",  label: "Vencidos",     color: "bg-orange-100 text-orange-800 border-orange-300" },
    { key: "reprovado",          label: "Cancelados",   color: "bg-rose-100 text-rose-700 border-rose-200" },
  ];
  const statusGrid = STATUS_GRID
    .map((s) => ({ ...s, count: counts[s.key] || 0 }))
    .filter((s) => s.count > 0);

  return (
    <div className="space-y-5">
      <FdpPromoCard fdp={fdp} onSent={(at) => setFdp({ ...fdp, interest_sent_at: at })} />
      {/* FDP progress card */}
      {fdp?.fdp_active && fdp?.state && (
        <div
          className="relative overflow-hidden rounded-2xl p-3.5 sm:p-4 text-white card-shadow"
          style={{ background: "linear-gradient(120deg, #312e81 0%, #4338ca 55%, #6366f1 100%)" }}
          data-testid="s-fdp-card"
        >
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                <Handshake className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[9px] font-bold tracking-widest uppercase text-white/80 leading-none">Sistema FDP</p>
                <p className="font-heading text-sm sm:text-base font-black leading-tight mt-0.5">Seu Faz Depois Paga</p>
              </div>
            </div>
            {fdp.state.active_budget_id && (
              <Link
                to={`/escola/orcamentos/${fdp.state.active_budget_id}`}
                data-testid="s-fdp-cta"
                className="shrink-0 inline-flex items-center gap-1 bg-white text-indigo-700 font-bold text-[11px] px-2.5 py-1 rounded-lg"
              >
                Ver orçamento FDP
                <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </div>
          <div className="mt-2.5">
            <div className="flex items-end justify-between flex-wrap gap-2 mb-1.5">
              <div>
                <p className="font-heading text-lg sm:text-xl font-black leading-none">{BRL(fdp.state.consumed)}</p>
                <p className="text-[11px] text-white/80 mt-0.5">de {BRL(fdp.state.max_value)} ({fdp.state.percent_value}%)</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold">{fdp.state.days_left} dias restantes</p>
                {fdp.state.end_date && (
                  <p className="text-[10px] text-white/70">até {formatDateBR(fdp.state.end_date)}</p>
                )}
              </div>
            </div>
            <div className="w-full h-1.5 rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, fdp.state.percent_value)}%`,
                  background: fdp.state.over_value ? "#fda4af" : fdp.state.near_value_limit ? "#fcd34d" : "#a7f3d0",
                }}
              />
            </div>
            {(fdp.state.near_value_limit || fdp.state.near_time_limit) && (
              <p className="mt-1.5 text-[10px] bg-white/15 rounded-md px-2 py-0.5 inline-block">
                ⚠️ Atenção: {fdp.state.over_value || fdp.state.near_value_limit ? "próximo ao valor máximo" : "período acabando"}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Hero — Faz, Depois Paga: só mostra se escola está ativa no sistema FDP */}
      {fdp?.fdp_active ? (
        <div className="relative overflow-hidden rounded-2xl card-shadow" data-testid="s-hero">
          <img
            src={HERO_IMAGE}
            alt=""
            className="w-full aspect-[16/9] sm:aspect-[21/9] md:aspect-[24/7] object-cover object-[left_30%]"
          />
          {/* Gradient escurecendo o lado direito (onde fica o texto), lado esquerdo transparente para mostrar o personagem */}
          <div className="absolute inset-0 bg-gradient-to-l from-indigo-900/95 via-indigo-900/60 to-transparent" />
          <div className="absolute inset-0 p-3 sm:p-5 flex flex-col justify-center text-white items-end text-right">
            <div className="max-w-[62%] sm:max-w-[55%]">
              <span className="inline-block bg-white/15 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest uppercase">
                Modalidade
              </span>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-[0.95] mt-1.5">
                FAZ,<br />
                <span style={{ color: "#facc15" }}>DEPOIS</span><br />
                <span className="text-white">PAGA</span>
              </h2>
              <p className="font-heading font-bold text-[10px] sm:text-xs mt-2 text-white/90 tracking-wide uppercase">
                Estaduais e municipais
              </p>
              <p className="text-[11px] sm:text-xs text-white/85 leading-snug mt-1">
                Manutenção simples e segura para sua escola.
              </p>
            </div>
          </div>
        </div>
      ) : (
        // Welcome hero (same style as admin dashboard) when school isn't FDP-active
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl card-shadow bg-slate-200" data-testid="s-welcome-hero">
          <img
            src={HERO_IMAGE}
            alt="Equipe AMC Escolas"
            className="w-full aspect-[16/9] sm:aspect-[21/9] md:aspect-[24/7] lg:aspect-[32/9] object-cover object-[center_25%]"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#1e3a8a]/90 via-[#1e3a8a]/55 to-transparent" />
          <div className="absolute inset-0 p-3 sm:p-4 md:p-6 flex flex-col justify-between text-white items-end text-right">
            <div className="max-w-[75%] sm:max-w-[65%]">
              <span className="inline-block bg-white/15 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold tracking-widest uppercase">
                Sistema AMC Escolas
              </span>
              <h2 className="font-heading text-lg sm:text-2xl md:text-3xl font-black tracking-tight leading-tight mt-1">
                Bem-vindo(a)!
              </h2>
              <p className="text-[10px] sm:text-xs md:text-sm text-white/90 leading-snug">
                Manutenção e serviços profissionais para a sua escola.
              </p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 max-w-[80%]">
              <p className="font-heading text-[11px] sm:text-sm font-bold leading-tight uppercase line-clamp-1">
                {me?.school?.name || "Sua escola"}
              </p>
              <p className="text-[10px] text-white/80 leading-tight">
                Diretor(a): {me?.user?.name || "—"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Price Consultation Banner — chamativo, acima dos quick actions */}
      <button
        type="button"
        onClick={() => setPriceOpen(true)}
        className="group relative w-full overflow-hidden rounded-2xl md:rounded-3xl text-left transition-all hover:shadow-2xl hover:-translate-y-0.5 active:scale-[0.99]"
        data-testid="s-btn-price-consultation"
      >
        <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 p-4 sm:p-5 md:p-6">
          {/* decorative shine */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/20 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-yellow-200/30 rounded-full blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/40 shrink-0">
              <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-bold text-white/90 uppercase tracking-wider">
                Novidade — base histórica
              </p>
              <h3 className="text-base sm:text-lg md:text-xl font-black text-white leading-tight mt-0.5">
                Consulta de itens ou serviço
              </h3>
              <p className="text-xs sm:text-sm text-white/90 mt-0.5 leading-snug">
                Veja quanto a AMC já cobrou por serviços parecidos. Sem compromisso.
              </p>
            </div>
            <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-xl bg-white/20 group-hover:bg-white/30 transition-colors shrink-0">
              <SearchIcon className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
      </button>

      {/* Quick actions strip — small cards right under the hero */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3" data-testid="s-quick-actions">
        <QuickCard
          testid="s-quick-orcamentos"
          icon={FileText}
          label="Orçamentos"
          sub={budgets.length ? String(budgets.length) : "Ver lista"}
          to="/escola/orcamentos"
          color="bg-[#7c3aed]/10 text-[#7c3aed]"
          badge={openCount > 0 ? openCount : null}
        />
        <QuickCard
          testid="s-quick-solicitar"
          icon={Phone}
          label="Solicitar"
          sub="Visita / orçamento"
          to="/escola/solicitar"
          color="bg-emerald-100 text-emerald-600"
        />
        {expiredCount > 0 && (
          <QuickCard
            testid="s-quick-vencidos"
            icon={AlarmClockOff}
            label="Vencidos"
            sub={`${expiredCount} para reabrir`}
            to="/escola/orcamentos/vencidos"
            color="bg-orange-100 text-orange-600"
            badge={expiredCount}
          />
        )}
        <QuickCard
          testid="s-quick-fotos"
          icon={ImageIcon}
          label="Fotos"
          sub="Serviços realizados"
          to="/escola/fotos"
          color="bg-sky-100 text-sky-600"
        />
        <QuickCard
          testid="s-quick-dados"
          icon={Edit3}
          label="Escola"
          sub="Dados cadastrais"
          to="/escola/dados"
          color="bg-amber-100 text-amber-600"
        />
        {!standalone ? (
          <QuickCard
            testid="s-quick-instalar"
            icon={Download}
            label="Instalar app"
            sub="Notificações"
            onClick={() => setInstallOpen(true)}
            color="bg-rose-100 text-rose-600"
          />
        ) : (
          <QuickCard
            testid="s-quick-ajuda"
            icon={HelpCircle}
            label="Ajuda"
            sub="Como usar"
            to="/escola/ajuda"
            color="bg-slate-100 text-slate-600"
          />
        )}
      </div>

      {/* Orçamentos por status (visão da escola) — mostra só os buckets que
          realmente existem.  Clique leva direto à listagem agrupada. */}
      {statusGrid.length > 0 && (
        <div
          className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 md:p-5"
          data-testid="s-status-grid"
        >
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-heading text-base font-bold text-[#1e3a8a] leading-tight">
              Orçamentos por status
            </h3>
            <Link
              to="/escola/orcamentos"
              className="text-[11px] font-semibold text-[#7c3aed] hover:underline inline-flex items-center gap-0.5"
              data-testid="s-status-grid-ver-todos"
            >
              Ver tudo <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {statusGrid.map((s) => (
              <Link
                key={s.key}
                to="/escola/orcamentos"
                data-testid={`s-status-card-${s.key}`}
                className={`rounded-xl border px-3 py-2.5 ${s.color} hover:brightness-105 transition-all`}
              >
                <p className="font-heading text-xl font-black leading-none">{s.count}</p>
                <p className="text-[10px] font-semibold mt-1 leading-tight">{s.label}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Serviços em aberto (orçamentos fiados) */}
      {fiados.length > 0 && (
        <div
          className="bg-white rounded-2xl border-l-4 border border-amber-300 border-l-amber-500 card-shadow p-4 md:p-5"
          data-testid="s-fiados-card"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <Handshake className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading text-base font-black text-slate-900 leading-tight">
                  Serviços em aberto
                </h3>
                <p className="text-[11px] text-slate-500">
                  {fiados.length} orçamento{fiados.length > 1 ? "s" : ""} aguardando pagamento
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">Total</p>
              <p className="font-heading text-lg font-black text-amber-700 leading-none mt-1">
                {BRL(fiadoTotal)}
              </p>
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {fiados.slice(0, 5).map((b) => (
              <li key={b.id}>
                <Link
                  to={`/escola/orcamentos/${b.id}`}
                  data-testid={`s-fiado-${b.id}`}
                  className="flex items-center justify-between gap-2 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-lg"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-bold text-slate-900 truncate">
                      Nº {b.number}
                      <span className="text-slate-400 font-normal mx-1.5">•</span>
                      <span className="text-[11px] text-slate-600 font-normal">{formatDateBR(b.budget_date)}</span>
                    </p>
                    <p className="text-[11px] text-slate-500 line-clamp-1">
                      {b.service_type || b.observation || `${b.items?.length || 0} itens`}
                    </p>
                  </div>
                  <p className="font-heading text-sm font-black text-amber-700 whitespace-nowrap">
                    {BRL(b.total || 0)}
                  </p>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
          {fiados.length > 5 && (
            <p className="text-[11px] text-slate-500 text-center pt-2 border-t border-slate-100 mt-2">
              + {fiados.length - 5} orçamento(s) em aberto
            </p>
          )}
        </div>
      )}

      {/* Budgets section — only 3 most recent + Ver todos */}
      <div className="bg-white rounded-2xl border border-slate-100 card-shadow overflow-hidden">
        <div className="p-4 md:p-5 flex items-start justify-between gap-3 border-b border-slate-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#7c3aed]" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-lg md:text-xl font-bold text-[#1e3a8a] leading-tight">
                Orçamentos recentes
              </h3>
              <p className="text-xs text-slate-500">
                Últimos {Math.min(3, budgets.length)} de {budgets.length} orçamento{budgets.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <Link
            to="/escola/orcamentos"
            data-testid="s-ver-todos-top"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border border-[#1e3a8a]/20 bg-[#1e3a8a]/5 text-xs font-semibold text-[#1e3a8a] hover:bg-[#1e3a8a]/10"
          >
            Ver todos <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            Nenhum orçamento emitido ainda.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto px-5 pb-3 pt-3">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left py-2 px-2 font-semibold">Nº do orçamento</th>
                    <th className="text-left py-2 px-2 font-semibold">Descrição</th>
                    <th className="text-left py-2 px-2 font-semibold">Data</th>
                    <th className="text-left py-2 px-2 font-semibold">Status</th>
                    <th className="text-right py-2 px-2 font-semibold">Valor</th>
                    <th className="text-right py-2 px-2 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recent.map((b) => {
                    const sm = STATUS_META[b.status] || STATUS_META.aguardando;
                    return (
                      <tr key={b.id} data-testid={`s-row-${b.number}`} className="hover:bg-slate-50">
                        <td className="py-3 px-2 font-mono font-bold text-[#1e3a8a]">#{b.number}</td>
                        <td className="py-3 px-2 text-slate-800">{b.observation?.split("\n")[0] || b.service_type || "—"}</td>
                        <td className="py-3 px-2 text-slate-600">{formatDateBR(b.budget_date)}</td>
                        <td className="py-3 px-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${sm.bg} ${sm.text}`}>
                            {sm.label}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right font-semibold">{BRL(b.total)}</td>
                        <td className="py-3 px-2 text-right">
                          <button
                            onClick={() => nav(`/escola/orcamentos/${b.id}`)}
                            data-testid={`s-view-${b.number}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#1e3a8a] text-white hover:bg-[#1e40af]"
                            aria-label="Visualizar"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-2">
              {recent.map((b) => {
                const sm = STATUS_META[b.status] || STATUS_META.aguardando;
                return (
                  <Link
                    key={b.id}
                    to={`/escola/orcamentos/${b.id}`}
                    data-testid={`s-card-${b.number}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:border-[#7c3aed]"
                  >
                    <div className={`w-10 h-10 rounded-xl ${sm.bg} flex items-center justify-center shrink-0`}>
                      <FileText className={`w-5 h-5 ${sm.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono font-bold text-[#1e3a8a]">#{b.number}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${sm.bg} ${sm.text}`}>
                          {sm.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 mt-0.5 truncate">{b.observation?.split("\n")[0] || b.service_type || "Orçamento"}</p>
                      <div className="flex items-center justify-between text-[11px] mt-1">
                        <span className="text-slate-500">{formatDateBR(b.budget_date)}</span>
                        <span className="font-semibold text-slate-900">{BRL(b.total)}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </Link>
                );
              })}
            </div>
          </>
        )}

        <div className="p-4 border-t border-slate-100">
          <Link
            to="/escola/orcamentos"
            data-testid="s-ver-todos-bottom"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-[#7c3aed]/5 border border-[#7c3aed]/20 text-sm font-semibold text-[#7c3aed] hover:bg-[#7c3aed]/10"
          >
            <FileText className="w-4 h-4" />
            Ver todos os orçamentos da escola
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Alert banner */}
      {firstWaiting && (
        <div
          className="relative overflow-hidden rounded-2xl p-5 border border-[#7c3aed]/20"
          style={{ background: "linear-gradient(100deg, #f5f3ff 0%, #ede9fe 100%)" }}
          data-testid="s-alert-waiting"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#7c3aed] flex items-center justify-center shrink-0 shadow-lg">
              <Shield className="w-6 h-6 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-base md:text-lg font-black text-[#1e3a8a]">
                Sua escola tem orçamento aguardando sua avaliação!
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                Clique no botão abaixo para acessar o orçamento.
              </p>
            </div>
            <Link
              to={`/escola/orcamentos/${firstWaiting.id}`}
              data-testid="s-alert-cta"
              className="hidden md:inline-flex shrink-0 bg-[#7c3aed] text-white font-semibold text-sm px-4 py-2 rounded-xl hover:bg-[#6d28d9]"
            >
              Ver orçamento
            </Link>
          </div>
          <Link
            to={`/escola/orcamentos/${firstWaiting.id}`}
            className="md:hidden mt-3 flex items-center justify-center bg-[#7c3aed] text-white font-semibold text-sm px-4 py-2.5 rounded-xl"
          >
            Ver orçamento
          </Link>
        </div>
      )}

      <InstallAppDialog open={installOpen} onOpenChange={setInstallOpen} />
      <PriceConsultationDialog open={priceOpen} onOpenChange={setPriceOpen} schoolName={me?.school_name || ""} />
    </div>
  );
}

const QuickCard = ({ icon: Icon, label, sub, to, onClick, color, testid, badge }) => {
  const content = (
    <>
      {badge != null && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow">
          {badge}
        </span>
      )}
      <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" strokeWidth={2.3} />
      </div>
      <div className="mt-2">
        <p className="font-heading font-bold text-xs md:text-sm text-[#1e3a8a] leading-tight line-clamp-1">
          {label}
        </p>
        <p className="text-[10px] md:text-[11px] text-slate-500 leading-tight line-clamp-1">
          {sub}
        </p>
      </div>
    </>
  );
  const base =
    "relative bg-white border border-slate-100 rounded-xl p-2.5 md:p-3 card-shadow hover:border-[#7c3aed]/40 hover:-translate-y-0.5 transition-all flex flex-col items-start min-h-[86px]";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testid} className={`${base} text-left w-full`}>
        {content}
      </button>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={base}>
      {content}
    </Link>
  );
};
